import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { ContactInfo, ContactLabels } from "@/components/landing/Contact/types";
import { faCommentDots, faEnvelope, faHeart } from "@fortawesome/pro-solid-svg-icons";
import { Icon } from "@/components/ui/Icon";

// Feature rows always ship in this fixed order, so the icon per row is
// positional rather than data-driven.
const FEATURE_ICONS: IconDefinition[] = [faCommentDots, faEnvelope, faHeart];

interface ContactInfoPanelProps {
	labels: ContactLabels;
	info: ContactInfo;
}

function ContactInfoPanel(prop: ContactInfoPanelProps) {
	const { labels, info } = prop;

	// The "email us anytime" row doubles as a real mailto link when an email
	// is configured, so the admin-entered contact info stays reachable.
	const rows = labels.features.map((feature, i) => ({
		...feature,
		icon: FEATURE_ICONS[i % FEATURE_ICONS.length],
		href: i === 1 && info.email ? `mailto:${info.email}` : undefined,
	}));

	return (
		<div>
			{labels.badge && (
				<span
					className="badge mb-6 px-3 py-1 text-xs tracking-[0.08em]"
					style={{
						background: "color-mix(in srgb, var(--brand-2) 16%, transparent)",
						color: "var(--brand-1)",
					}}
				>
					{labels.badge.toUpperCase()}
				</span>
			)}

			<h2 className="text-4xl font-extrabold tracking-tight sm:text-5xl">{labels.title}</h2>
			<p className="mt-4 max-w-md text-lg leading-relaxed text-muted-foreground">{labels.intro}</p>

			<div className="mt-10 flex flex-col gap-6">
				{rows.map((row) => {
					const Tag = row.href ? "a" : "div";
					return (
						<Tag
							key={row.title}
							{...(row.href ? { href: row.href } : {})}
							className="flex items-start gap-4"
						>
							<span
								className="grid h-11 w-11 shrink-0 place-items-center rounded-xl"
								style={{
									background: "color-mix(in srgb, var(--brand-2) 14%, transparent)",
									color: "var(--brand-1)",
								}}
							>
								<Icon icon={row.icon} size={19} />
							</span>
							<div>
								<p className="font-semibold">{row.title}</p>
								<p className="mt-1 text-sm leading-relaxed text-muted-foreground">{row.body}</p>
							</div>
						</Tag>
					);
				})}
			</div>
		</div>
	);
}

export default ContactInfoPanel;
